import React from 'react';

import FormKeys from 'modules/form/types/FormKeys';

import { useSelector } from 'hooks';

import { getFormValuesByKeys } from 'modules/form/selectors';
import { getLastUpdatedTime } from 'modules/sockets/selectors';

/**
 * @description Returns the initial values for a form based on the form key(s) provided.
 * - If there is only one form key, it returns the initial value for that form key.
 * - If there are multiple form keys, it returns the initial value for each form key, wrapped within an object.
 * @example
 * useInitialValues('repairOrder', 'extras');
 * // Returns:
 * // {
 * //   repairOrder: { /* 'repairOrder' initial value *\/ },
 * //   extras: { /* 'extras' initial value *\/ }
 * // }
 */
const useInitialValues = <T extends { [key in FormKeys]: U } | U, U>(...formKeys: Array<FormKeys>): T => {
  const lastUpdatedTime = useSelector(getLastUpdatedTime);
  const initialValues = useSelector((state) => getFormValuesByKeys(state, formKeys)) as Array<{ [key in FormKeys]: U }>;

  return React.useMemo(
    () => {
      if (formKeys.length < 2) {
        return formKeys.length === 0 ? undefined : initialValues[0] as T;
      }

      return initialValues.reduce((acc: T, initialValue, index) => ({
        ...acc,
        [formKeys[index]]: initialValue,
      }), {} as T);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lastUpdatedTime],
  );
};

export default useInitialValues;
